import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "item",
    "bar",
    "srComplete",
    "srProgress",
    "srProgressValue",
  ];

  static values = {
    count: Number,
    redirectUrl: String,
  };

  declare countValue: number;
  declare currentStepIndex: number;
  declare progress: Record<number, number>;
  declare steps: Array<number>;
  declare itemTargets: Array<HTMLElement>;
  declare lastAnnouncedStep: number;
  declare srProgressValueTarget: HTMLElement;
  declare srProgressTarget: HTMLElement;
  declare srCompleteTarget: HTMLElement;
  declare redirectUrlValue: string;
  declare timeoutIds: Array<ReturnType<typeof setTimeout>>;

  connect() {
    this.progress = {};
    this.currentStepIndex = 0;
    for (let i = 0; i < this.countValue; i++) {
      this.progress[i] = 0;
    }
    this.steps = Array(this.countValue)
      .fill(0)
      .map((_, i) => i);
    this.timeoutIds = [];
    this.analyzeStep();
    this.lastAnnouncedStep = -1;
  }

  updateUI(stepIndex: number) {
    const stepEl = this.itemTargets[stepIndex];
    const bar = stepEl.querySelector(".bm-steps-fill") as HTMLElement;
    const percent = stepEl.querySelector(".bm-steps-percentage") as HTMLElement;
    const value = this.progress[stepIndex];
    const offset = 100 - value;

    if (bar) bar.style.transform = `translateX(-${offset}%)`;
    if (percent) percent.textContent = `${Math.round(value)}%`;

    // Only update screen reader progress when a step completes
    if (
      this.progress[stepIndex] >= 100 &&
      this.lastAnnouncedStep !== stepIndex
    ) {
      this.lastAnnouncedStep = stepIndex;

      const totalProgress = this.steps.reduce(
        (sum, i) => sum + this.progress[i],
        0
      );
      const averageProgress = totalProgress / this.countValue;
      const rounded = Math.round(averageProgress);

      this.srProgressValueTarget.textContent = `${rounded}`;

      if (this.areStepsComplete()) {
        this.srProgressTarget.classList.add("d-none");
        this.srCompleteTarget.classList.remove("d-none");
      }
    }

    if (this.areStepsComplete()) {
      window.location.href = this.redirectUrlValue;
    }
  }
  disconnect(): void {
    this.timeoutIds.forEach(clearTimeout);
    this.timeoutIds = [];
  }

  analyzeStep() {
    const stepIndex = this.currentStepIndex;
    const totalStepTime = 4000 / this.countValue;
    const startTime = Date.now();

    const update = () => {
      const elapsed = Date.now() - startTime;
      const progressRatio = elapsed / totalStepTime;
      this.progress[stepIndex] = Math.min(progressRatio * 100, 100);
      this.updateUI(stepIndex);

      if (this.progress[stepIndex] < 100) {
        const id = setTimeout(update, 50);
        this.timeoutIds.push(id);
      } else {
        this.currentStepIndex++;
        if (this.currentStepIndex < this.countValue) {
          const id = setTimeout(() => this.analyzeStep(), 5);
          this.timeoutIds.push(id);
        }
      }
    };

    update();
  }

  areStepsComplete(): boolean {
    return this.progress[this.countValue - 1] == 100;
  }
}
